/** A folder as the tree needs it: who it is, what holds it, what it is called. */
export type TreeFolder = { id: string; parentId: string | null; name: string }

/**
 * What can be dragged or moved: a document, by its kind and id, or a folder. It
 * carries its name, so the sentence reporting a move needs nothing else, and
 * the space it is in, since a shelf may show several and a move is within one.
 */
export type Movable =
  | {
      kind: 'book' | 'page' | 'deck' | 'board'
      id: string
      name: string
      spaceId: string
      folderId: string | null
    }
  | {
      kind: 'folder'
      id: string
      name: string
      spaceId: string
      parentId: string | null
    }

/**
 * A space's folders read as a tree. Built once per list, so every question a
 * screen asks of the tree is answered from the same reading. `spaceId` is the
 * space they are in, or null for folders gathered from several, which nothing
 * lands in.
 */
export function folderTree<F extends TreeFolder>(
  folders: readonly F[],
  spaceId: string | null,
) {
  const byId = new Map(folders.map((f) => [f.id, f]))
  const byName = (a: F, b: F) => a.name.localeCompare(b.name)
  const childrenOf = (parentId: string | null): F[] =>
    folders.filter((f) => (f.parentId ?? null) === parentId).sort(byName)

  /** The folders from the top down to this one, itself last. A broken chain
      stops rather than looping, since a cycle is refused by the server. */
  const pathOf = (id: string): F[] => {
    const path: F[] = []
    const seen = new Set<string>()
    let at = byId.get(id)
    while (at && !seen.has(at.id)) {
      seen.add(at.id)
      path.unshift(at)
      at = at.parentId ? byId.get(at.parentId) : undefined
    }
    return path
  }

  /** This folder and every folder inside it, at any depth. */
  const within = (id: string): Set<string> => {
    const out = new Set<string>([id])
    const walk = (parent: string) => {
      for (const child of childrenOf(parent)) {
        if (out.has(child.id)) continue
        out.add(child.id)
        walk(child.id)
      }
    }
    walk(id)
    return out
  }

  /** Every folder in reading order — each under its parent — with its depth. */
  const flat = (): { folder: F; depth: number }[] => {
    const out: { folder: F; depth: number }[] = []
    const walk = (parent: string | null, depth: number) => {
      for (const f of childrenOf(parent)) {
        out.push({ folder: f, depth })
        walk(f.id, depth + 1)
      }
    }
    walk(null, 0)
    return out
  }

  /**
   * Whether something may land in a folder (null: Unfiled for a document,
   * the top for a folder). Only something of the same space, not where it
   * already is, and a folder never into itself or anything inside it — the
   * server refuses each, and a target that lit up for them would be promising
   * a move that cannot happen. Crossing into another space is a move of its
   * own, confirmed, never a drop.
   */
  const canLand = (item: Movable, target: string | null): boolean => {
    if (spaceId === null || item.spaceId !== spaceId) return false
    if (item.kind !== 'folder') return item.folderId !== target
    if (item.parentId === target) return false
    return target === null || !within(item.id).has(target)
  }

  return { spaceId, byId, childrenOf, pathOf, within, flat, canLand }
}

export type FolderTree<F extends TreeFolder = TreeFolder> = ReturnType<
  typeof folderTree<F>
>
